import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@nyce/admin-kit/api';
import { Button, Chip, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  EmptyState,
  ErrorBox,
  Loading,
  SortSelect,
  SortTh,
  TableScroller,
  Tabs,
  useAsync,
  useSort,
  type SortKeys,
} from '../../components/common';
import { stats, type PerfSeries, type RawSeries, type TopMethod, type Volume } from '../../api/stats';
import { formatCount, formatDuration, formatRelative } from '../../lib/format';
import {
  BarChart,
  LineChart,
  ScatterChart,
  SegmentedControl,
  Sparkline,
  type LineSeries,
  type Marker,
} from '@nyce/admin-kit/console';
import {
  RangeTabs,
  fmtDay,
  fmtInt,
  fmtMs,
  isShortRange,
  rangeWindow,
  statsError,
  useParam,
  useRange,
  useRefreshTick,
  type Window,
} from './shared';
import {
  LARGE_CALLS,
  LARGE_CHANGE,
  MIN_ZOOM_MS,
  POINTS_DEFAULT_HOURS,
  bucketLabel,
  bucketMs,
  callTotals,
  canShowPoints,
  compareWindow,
  fmtChange,
  methodChanges,
  perfBucket,
  periodLabel,
  periodWindow,
  zoomWindow,
  type CompareKey,
  type MethodChange,
  type PerfBucket,
} from './compare';

// Sections shared by the organization and installation pages.

const MAX_METHODS = 6;
/** `?methods=-`: none charted (no `?methods=` = the default four, so unticking the last one must not bring them back). */
const NO_METHODS = '-';
type Metric = 'p95' | 'avg' | 'median' | 'p99' | 'max';
const METRICS: { value: Metric; label: string }[] = [
  { value: 'p95', label: 'p95' },
  { value: 'median', label: 'Median' },
  { value: 'avg', label: 'Average' },
  { value: 'p99', label: 'p99' },
  { value: 'max', label: 'Max' },
];

/** The top methods (by total time from the collector) sorted on the page. */
const METHOD_SORT: SortKeys<TopMethod> = {
  method: { label: 'Method', value: (m) => m.method },
  calls: { label: 'Calls', value: (m) => m.count, desc: true },
  avg: { label: 'Average', value: (m) => m.avg, desc: true },
  p95: { label: 'p95', value: (m) => m.p95, desc: true },
  max: { label: 'Max', value: (m) => m.max, desc: true },
  total: { label: 'Total time', value: (m) => m.totalMs, desc: true },
};

const toSeries = (
  list: PerfSeries[],
  pick: (p: PerfSeries['points'][number]) => number | null,
  methods: string[],
  compare?: { shiftMs: number; label: string }
): LineSeries[] =>
  list.map((s) => ({
    key: compare ? `${s.method}|vs` : s.method,
    label: compare ? `${s.method} · ${compare.label}` : s.method,
    points: s.points.map((p) => ({ t: Date.parse(p.t) + (compare?.shiftMs ?? 0), v: pick(p) })),
    // the method keeps its colour in both periods
    color: Math.max(0, methods.indexOf(s.method)),
    dashed: !!compare,
  }));

const COMPARE: { value: string; label: string }[] = [
  { value: '', label: 'No comparison' },
  { value: 'prev', label: 'vs previous period' },
  { value: 'year', label: 'vs a year earlier' },
  { value: 'day', label: 'vs period ending…' },
];

const todayUtc = () => new Date().toISOString().slice(0, 10);

const CALLS_PER: Record<PerfBucket, string> = {
  '1m': 'minute',
  '5m': '5 minutes',
  '15m': '15 minutes',
  '1h': 'hour',
  '1d': 'day',
};

/** The coarser of two buckets: both periods of a comparison are drawn with the same one. */
const coarser = (a: PerfBucket, b: PerfBucket | null) => (b && bucketMs(b) > bucketMs(a) ? b : a);

/**
 * `performance/series` in `bucket`; a sub-hour bucket the collector cannot serve (older collector: 400 `invalid`, raw
 * measurements gone: `values_expired`) falls back to hourly points.
 */
async function seriesIn(
  q: Omit<Parameters<typeof stats.series>[0], 'bucket'>,
  bucket: PerfBucket
): Promise<{ list: PerfSeries[]; bucket: PerfBucket }> {
  try {
    return { list: await stats.series({ ...q, bucket }), bucket };
  } catch (e) {
    const fine = bucket !== '1h' && bucket !== '1d';
    if (!fine || !(e instanceof ApiError) || (e.code !== 'invalid' && e.code !== 'values_expired')) throw e;
    return { list: await stats.series({ ...q, bucket: '1h' }), bucket: '1h' };
  }
}

/** The raw measurements; null when the collector has none to give (older collector: 404, expired: 400). */
async function pointsOf(q: Parameters<typeof stats.points>[0]): Promise<RawSeries[] | null> {
  try {
    return await stats.points(q);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.code === 'values_expired' || e.code === 'invalid'))
      return null;
    throw e;
  }
}

/** How often a live short window (1 h / 4 h / 12 h ending now) reloads. */
const liveMs = (hours: number) => (hours <= 1 ? 30_000 : 60_000);

type View = '' | 'lines' | 'points';

/** The exact times for `performance/top` when the window is not whole UTC days and fits its 31 days. */
const topTimes = (w: Window) =>
  (Date.parse(w.from) % 86_400_000 !== 0 || Date.parse(w.to) % 86_400_000 !== 0) && w.hours <= 31 * 24
    ? { fromTime: w.from, toTime: w.to }
    : {};

/** The change cell: slower / faster chips for a large change, else the change muted; the other period's figures as tooltip. */
function ChangeCell({ c }: { c: MethodChange | undefined }) {
  if (!c) return <span className="nyk-ui-hint">—</span>;
  const b = c.before;
  const title = b
    ? `Other period: ${fmtInt(b.count)} calls, average ${fmtMs(b.avg)}, p95 ${fmtMs(b.p95)}, max ${fmtMs(b.max)}${
        c.basis === 'avg' ? ' (compared on the average: no p95 in one period)' : ''
      }`
    : 'Not among the other period’s 50 methods with the most total time';
  if (c.flag === 'new')
    return (
      <Chip size="sm" className="oph-tone nyk-tone-grey" title={title}>
        new
      </Chip>
    );
  if (c.flag === 'slower' || c.flag === 'faster')
    return (
      <Chip
        size="sm"
        className={`oph-tone nyk-tone-${c.flag === 'slower' ? 'red' : 'green'}`}
        title={title}
        data-flag={c.flag}
      >
        {c.flag} {fmtChange(c.change)}
      </Chip>
    );
  return (
    <span className="nyk-ui-hint" title={title}>
      {fmtChange(c.change)}
    </span>
  );
}

/** Many more / far fewer calls as a chip, else the change muted; the call counts of both periods as tooltip. */
function CallsChangeCell({ c, now }: { c: MethodChange | undefined; now: number }) {
  if (!c?.before) return <span className="nyk-ui-hint">—</span>;
  const title = `${fmtInt(now)} calls, other period ${fmtInt(c.before.count)}`;
  if (c.callsFlag)
    return (
      <Chip
        size="sm"
        className={`oph-tone nyk-tone-${c.callsFlag === 'more' ? 'blue' : 'amber'}`}
        title={title}
        data-calls-flag={c.callsFlag}
      >
        {c.callsFlag === 'more' ? 'more' : 'fewer'} {fmtChange(c.callsChange)}
      </Chip>
    );
  return (
    <span className="nyk-ui-hint" title={title}>
      {fmtChange(c.callsChange)}
    </span>
  );
}

/**
 * Method performance of an organization (`ALL` = every WorkServer together) or one installation: the methods ranked
 * over the range, and the picked ones (default: the four with the most total time) as time series. Durations and call
 * counts are separate charts — never two y-axes. The period ends now or on a chosen day (`?end=`); `?vs=` compares it
 * with another of the same length (dashed lines on the same axis, a change column, large changes flagged). Dragging
 * across a chart zooms into that span (`?from=&to=`, both charts, the table and the comparison follow); short spans
 * get minute points from the collector's raw measurements, and up to 2 h can show the single calls as dots (`?view=`,
 * the default below 30 min). The 1 h / 4 h / 12 h presets ending now refresh themselves while the page is visible.
 */
export function PerformanceSection({
  customerId,
  workserver = 'ALL',
  markers,
}: {
  customerId: number;
  workserver?: string;
  markers?: Marker[];
}) {
  const [range, setRange] = useRange('7d', true);
  const [auto, setAuto] = useState(true);
  const [view, setView] = useParam<View>('view', '');
  const [metric, setMetric] = useParam<Metric>('metric', 'p95');
  const [picked, setPicked] = useParam('methods', '');
  const [end, setEnd] = useParam('end', '');
  const [vs, setVs] = useParam<CompareKey>('vs', '');
  const [zoomFrom, setZoomFrom] = useParam('from', '');
  const [zoomTo, setZoomTo] = useParam('to', '');
  const zoom = useMemo(() => zoomWindow(zoomFrom, zoomTo), [zoomFrom, zoomTo]);
  const live = !zoom && !end && isShortRange(range);
  const tick = useRefreshTick(liveMs(periodWindow(range, '').hours), live && auto);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- a tick moves a live window on to now
  const w = useMemo(() => zoom ?? periodWindow(range, end), [zoom, range, end, tick]);
  const cw = useMemo(() => compareWindow(range, w, vs, !!zoom), [range, w, vs, zoom]);
  const setZoom = (a: number | null, b?: number) => {
    // whole minutes, so the link stays readable and repeated loads hit the same buckets
    setZoomFrom(a == null ? '' : new Date(Math.floor(a / 60_000) * 60_000).toISOString());
    setZoomTo(a == null || b == null ? '' : new Date(Math.ceil(b / 60_000) * 60_000).toISOString());
  };
  const top = useAsync(
    () =>
      stats.top({
        customerId,
        workserver,
        from: w.fromDay,
        to: w.toDay,
        ...topTimes(w),
        sort: 'total',
        limit: 50,
      }),
    [customerId, workserver, w.from, w.to, tick]
  );
  const topBefore = useAsync(
    () =>
      cw
        ? stats.top({
            customerId,
            workserver,
            from: cw.fromDay,
            to: cw.toDay,
            ...topTimes(cw),
            sort: 'total',
            limit: 50,
          })
        : Promise.resolve(null),
    [customerId, workserver, cw?.from, cw?.to]
  );
  const wanted = perfBucket(w);
  const bucket = coarser(wanted, cw && perfBucket(cw));
  const methods =
    picked === NO_METHODS
      ? []
      : picked
        ? picked.split('|').filter(Boolean)
        : (top.data ?? []).slice(0, 4).map((m) => m.method);
  const key = methods.join('|');
  const series = useAsync(
    () =>
      methods.length
        ? seriesIn({ customerId, workserver, method: methods, from: w.from, to: w.to }, bucket)
        : Promise.resolve({ list: [] as PerfSeries[], bucket }),
    [customerId, workserver, key, w.from, w.to, bucket, tick]
  );
  const pointsOk = canShowPoints(w);
  const wantPoints = pointsOk && (view === 'points' || (view === '' && w.hours <= POINTS_DEFAULT_HOURS));
  const points = useAsync(
    () =>
      wantPoints && methods.length
        ? pointsOf({ customerId, workserver, method: methods, from: w.from, to: w.to })
        : Promise.resolve(null),
    [customerId, workserver, key, w.from, w.to, wantPoints, tick]
  );
  // the collector may not have them (older version, expired): the duration chart stays a line chart
  const showPoints = wantPoints && (points.loading || points.data != null);
  // the other period in the bucket the shown one got (an older collector answers hourly)
  const shownBucket = series.data?.bucket ?? bucket;
  const seriesBefore = useAsync(
    () =>
      cw && methods.length
        ? seriesIn({ customerId, workserver, method: methods, from: cw.from, to: cw.to }, shownBucket)
        : Promise.resolve({ list: [] as PerfSeries[], bucket: shownBucket }),
    [customerId, workserver, key, cw?.from, cw?.to, shownBucket]
  );
  const changes = useMemo(
    () => (cw && top.data && topBefore.data ? methodChanges(top.data, topBefore.data) : null),
    [cw, top.data, topBefore.data]
  );
  const sortKeys = useMemo<SortKeys<TopMethod>>(
    () =>
      changes
        ? {
            ...METHOD_SORT,
            change: { label: 'p95 change', value: (m) => changes.get(m.method)?.change, desc: true },
            callsChange: {
              label: 'Calls change',
              value: (m) => changes.get(m.method)?.callsChange,
              desc: true,
            },
          }
        : METHOD_SORT,
    [changes]
  );
  const sort = useSort(top.data, sortKeys, useParam('msort', ''));
  const toggle = (m: string) => {
    const next = methods.includes(m) ? methods.filter((x) => x !== m) : [...methods, m].slice(-MAX_METHODS);
    setPicked(next.length ? next.join('|') : NO_METHODS);
  };
  const from = Date.parse(w.from);
  const to = Date.parse(w.to);
  const step = bucketMs(shownBucket);
  const data = series.data?.list ?? [];
  const cmp = cw ? { shiftMs: from - Date.parse(cw.from), label: periodLabel(cw) } : undefined;
  const before = cmp ? (seriesBefore.data?.list ?? []) : [];
  const exactTop = 'fromTime' in topTimes(w);
  const select = (a: number, b: number) => setZoom(a, b);
  const lines = (pick: (p: PerfSeries['points'][number]) => number | null) => [
    ...toSeries(data, pick, methods),
    ...(cmp ? toSeries(before, pick, methods, cmp) : []),
  ];
  const flagged = changes
    ? [...changes.values()].filter((c) => c.flag === 'slower' || c.flag === 'faster')
    : [];
  const slower = flagged.filter((c) => c.flag === 'slower').sort((a, b) => (b.change ?? 0) - (a.change ?? 0));
  const faster = flagged.filter((c) => c.flag === 'faster');
  const all = changes ? [...changes.values()] : [];
  const byCalls = (a: MethodChange, b: MethodChange) =>
    Math.abs(b.callsChange ?? 0) - Math.abs(a.callsChange ?? 0);
  const more = all.filter((c) => c.callsFlag === 'more').sort(byCalls);
  const fewer = all.filter((c) => c.callsFlag === 'fewer').sort(byCalls);
  const totals = changes && top.data ? callTotals(all, top.data) : null;
  const named = (list: MethodChange[]) =>
    list
      .slice(0, 5)
      .map((c) => `${c.method} ${fmtChange(c.callsChange)}`)
      .join(', ') + (list.length > 5 ? ` and ${list.length - 5} more` : '');
  // a live refresh does not dim the charts
  const loading = (series.loading || seriesBefore.loading || points.loading) && !(live && auto);
  const err = top.error ?? topBefore.error ?? series.error ?? seriesBefore.error ?? points.error;
  const dots = (points.data ?? []).map((s) => ({
    key: s.method,
    label: s.method,
    color: Math.max(0, methods.indexOf(s.method)),
    points: s.points.map((p) => ({ t: Date.parse(p.t), v: p.duration, count: p.count })),
  }));
  const truncated = (points.data ?? []).filter((s) => s.truncated).map((s) => s.method);
  return (
    <section className="sx-body" data-testid="performance">
      <div className="sx-toolbar">
        <RangeTabs
          short
          value={zoom ? ('' as typeof range) : range}
          onChange={(v) => {
            setRange(v);
            setZoom(null);
          }}
        />
        {live && (
          <Switch checked={auto} onChange={setAuto} label={`Auto-refresh ${liveMs(w.hours) / 1000} s`} />
        )}
        {zoom && (
          <Button size="sm" variant="secondary" onClick={() => setZoom(null)} data-testid="perf-reset-zoom">
            Reset zoom
          </Button>
        )}
        <span className="sx-labelled">
          <span className="nyk-ui-hint" aria-hidden="true">
            ending
          </span>
          <TextField
            type="date"
            aria-label="Period ending"
            title="Last day of the period (UTC); empty = up to now"
            value={end}
            max={todayUtc()}
            onChange={(e) => {
              setEnd(e.target.value);
              setZoom(null);
            }}
          />
        </span>
        <Select
          aria-label="Compare with"
          value={vs && vs !== 'prev' && vs !== 'year' ? 'day' : vs}
          onChange={(v) =>
            setVs(v === 'day' ? (compareWindow(range, w, 'prev')?.toDay ?? '') : (v as CompareKey))
          }
          options={COMPARE}
        />
        {vs && vs !== 'prev' && vs !== 'year' && (
          <span className="sx-labelled">
            <span className="nyk-ui-hint" aria-hidden="true">
              ending
            </span>
            <TextField
              type="date"
              aria-label="Other period ending"
              value={vs}
              max={todayUtc()}
              onChange={(e) => setVs(e.target.value || 'prev')}
            />
          </span>
        )}
        {pointsOk && (
          <SegmentedControl<'lines' | 'points'>
            label="Durations as"
            value={wantPoints ? 'points' : 'lines'}
            onChange={setView}
            options={[
              { value: 'lines', label: 'Lines', title: 'A statistic per bucket' },
              { value: 'points', label: 'Single calls', title: 'Each measurement as a dot' },
            ]}
          />
        )}
        {!showPoints && (
          <Select
            aria-label="Duration statistic"
            value={metric}
            onChange={(v) => setMetric(v as Metric)}
            options={METRICS}
          />
        )}
        <span className="nyk-ui-hint" data-testid="perf-periods">
          {workserver === 'ALL' ? 'All WorkServers together' : workserver} · {periodLabel(w)}
          {cw && ` vs ${periodLabel(cw)}`} · {bucketLabel(shownBucket)}
          {shownBucket !== wanted &&
            wanted !== '1h' &&
            wanted !== '1d' &&
            ' (finer points are not available here)'}{' '}
          · UTC
          {!zoom && ' · drag across a chart to zoom'}
          {wantPoints && !points.loading && points.data == null && ' · single calls are not available here'}
          {truncated.length > 0 && ` · the slowest calls only for ${truncated.join(', ')}`}
        </span>
      </div>
      <ErrorBox error={err ? statsError(err) : null} />
      <div className={`sx-grid-2${loading ? ' oph-usage-stale' : ''}`}>
        <div className="nyk-card">
          {showPoints ? (
            <ScatterChart
              title="Duration of each call"
              series={dots}
              from={from}
              to={to}
              format={fmtMs}
              empty={
                points.loading
                  ? 'Loading…'
                  : methods.length
                    ? 'No calls of these methods in this range.'
                    : 'Pick methods below.'
              }
              onSelect={select}
              minSelectMs={MIN_ZOOM_MS}
            />
          ) : (
            <LineChart
              title={`${METRICS.find((m) => m.value === metric)?.label} duration`}
              series={lines((p) => p[metric])}
              from={from}
              to={to}
              stepMs={step}
              format={fmtMs}
              markers={markers}
              dashedLabel={cmp && `dashed: ${cmp.label}`}
              empty={methods.length ? 'No calls of these methods in this range.' : 'Pick methods below.'}
              onSelect={select}
              minSelectMs={MIN_ZOOM_MS}
            />
          )}
        </div>
        <div className="nyk-card">
          <LineChart
            title={`Calls per ${CALLS_PER[shownBucket]}`}
            series={lines((p) => p.count)}
            from={from}
            to={to}
            stepMs={step}
            format={formatCount}
            markers={markers}
            dashedLabel={cmp && `dashed: ${cmp.label}`}
            empty={methods.length ? 'No calls of these methods in this range.' : 'Pick methods below.'}
            onSelect={select}
            minSelectMs={MIN_ZOOM_MS}
          />
        </div>
      </div>
      {changes && (
        <div className={`nyk-notice${slower.length ? ' nyk-notice-warn' : ''}`} data-testid="perf-changes">
          {slower.length ? (
            <>
              <b>
                {slower.length} {slower.length === 1 ? 'method' : 'methods'} slower
              </b>{' '}
              than {periodLabel(cw!)}: {slower.map((c) => `${c.method} ${fmtChange(c.change)}`).join(', ')}.
            </>
          ) : (
            <>No method got much slower than {periodLabel(cw!)}.</>
          )}
          {faster.length > 0 && ` ${faster.length} faster.`}{' '}
          {totals && totals.methods > 0 && (
            <div data-testid="perf-calls">
              <b>Calls</b> {fmtInt(totals.now)} vs {fmtInt(totals.before)} ({fmtChange(totals.change)}) over
              the {totals.methods} methods in both periods.
              {more.length > 0 && ` Many more: ${named(more)}.`}
              {fewer.length > 0 && ` Far fewer: ${named(fewer)}.`}
            </div>
          )}
          <span className="nyk-ui-hint">
            Large = p95 {fmtChange(LARGE_CHANGE.pct)} or more and at least {LARGE_CHANGE.minMs} ms, with{' '}
            {LARGE_CHANGE.minCalls}+ calls in both periods; calls {fmtChange(LARGE_CALLS.pct)} /{' '}
            {fmtChange(-LARGE_CALLS.pct)} and at least {LARGE_CALLS.minDiff} apart.
          </span>
        </div>
      )}
      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Methods by total time</h2>
          <span className="nyk-ui-hint" data-testid="perf-table-span">
            {exactTop ? periodLabel(w) : `UTC days ${w.fromDay} – ${w.toDay}`}; tick up to {MAX_METHODS} to
            chart them
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={!methods.length}
            onClick={() => setPicked(NO_METHODS)}
          >
            Deselect all
          </Button>
          <SortSelect sort={sort} />
        </div>
        <Loading loading={top.loading && !top.data} />
        {top.data && !top.data.length && <EmptyState title="No method timings in this range." />}
        {!!top.data?.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="top-methods" aria-label="Methods">
              <thead>
                <tr>
                  <th aria-label="Chart" />
                  <SortTh sort={sort} by="method" />
                  <SortTh sort={sort} by="calls" className="sx-num" />
                  <SortTh sort={sort} by="avg" className="sx-num" />
                  <SortTh sort={sort} by="p95" className="sx-num" />
                  <SortTh sort={sort} by="max" className="sx-num" />
                  <SortTh sort={sort} by="total" className="sx-num" />
                  {changes && <SortTh sort={sort} by="change" className="sx-num" />}
                  {changes && <SortTh sort={sort} by="callsChange" className="sx-num" />}
                </tr>
              </thead>
              <tbody>
                {sort.rows.map((m) => {
                  const c = changes?.get(m.method);
                  return (
                    <tr
                      key={m.method}
                      data-method={m.method}
                      className={c?.flag === 'slower' ? 'sx-slower' : undefined}
                    >
                      <td>
                        <input
                          type="checkbox"
                          className="sx-check"
                          aria-label={`Chart ${m.method}`}
                          checked={methods.includes(m.method)}
                          onChange={() => toggle(m.method)}
                        />
                      </td>
                      <td className="nyk-card-title nyk-wrap">{m.method}</td>
                      <td data-label="Calls" className="sx-num">
                        {fmtInt(m.count)}
                      </td>
                      <td data-label="Average" className="sx-num">
                        {fmtMs(m.avg)}
                      </td>
                      <td data-label="p95" className="sx-num">
                        {fmtMs(m.p95)}
                      </td>
                      <td data-label="Max" className="sx-num">
                        {fmtMs(m.max)}
                      </td>
                      <td data-label="Total time" className="sx-num">
                        {formatDuration(m.totalMs)}
                      </td>
                      {changes && (
                        <td data-label="p95 change" className="sx-num">
                          <ChangeCell c={c} />
                        </td>
                      )}
                      {changes && (
                        <td data-label="Calls change" className="sx-num">
                          <CallsChangeCell c={c} now={m.count} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroller>
        )}
        {cw && topBefore.loading && <Loading loading />}
      </div>
    </section>
  );
}

/** Daily totals of one volume type: `transaction` has a `total` code next to the per-type codes. */
export function dailyTotals(rows: Volume[], type: string): { date: string; count: number }[] {
  const of = rows.filter((r) => r.type === type);
  const hasTotal = of.some((r) => r.code === 'total');
  const byDay = new Map<string, number>();
  for (const r of of)
    if (!hasTotal || r.code === 'total') byDay.set(r.date, (byDay.get(r.date) ?? 0) + r.count);
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date, count }));
}

const VOLUME_LABELS: Record<string, string> = {
  transaction: 'Transactions',
  delivery: 'Deliveries',
  deliveryline: 'Delivery lines',
  shipmentreference: 'Shipment references',
  warehouseorder: 'Warehouse orders',
  warehouseorderline: 'Warehouse order lines',
  purchasedelivery: 'Purchase deliveries',
  purchasedeliveryline: 'Purchase delivery lines',
};
export const volumeLabel = (t: string) => VOLUME_LABELS[t] ?? t;

/**
 * Daily volumes and licence usage, one report per organization and day (the collector removes the duplicates every
 * WorkServer sends). Licences: one line per licence type.
 */
export function VolumesSection({ customerId, workserver }: { customerId: number; workserver?: string }) {
  const [range, setRange] = useParam<'30d' | '90d' | '1y'>('vrange', '30d');
  const [type, setType] = useParam('vtype', 'transaction');
  const w = useMemo(() => rangeWindow(range), [range]);
  const volumes = useAsync(
    () => stats.volumes({ customerId, workserver, from: w.fromDay, to: w.toDay }),
    [customerId, workserver, w.fromDay, w.toDay]
  );
  const licences = useAsync(
    () => (workserver ? Promise.resolve([]) : stats.licences({ customerId, from: w.fromDay, to: w.toDay })),
    [customerId, workserver, w.fromDay, w.toDay]
  );
  const types = [...new Set((volumes.data ?? []).map((v) => v.type))].sort();
  const totals = dailyTotals(volumes.data ?? [], type);
  const licenceTypes = [...new Set((licences.data ?? []).map((l) => l.type))].sort();
  const licenceSeries: LineSeries[] = licenceTypes.map((t) => ({
    key: t,
    label: t || 'licences',
    points: (licences.data ?? [])
      .filter((l) => l.type === t)
      .map((l) => ({ t: Date.parse(`${l.date}T00:00:00Z`), v: l.count })),
  }));
  const latest = licenceTypes.map((t) => {
    const rows = (licences.data ?? []).filter((l) => l.type === t);
    return { type: t, last: rows[rows.length - 1], peak: Math.max(...rows.map((r) => r.count)) };
  });
  return (
    <section className="sx-body" data-testid="volumes">
      <div className="sx-toolbar">
        <Tabs
          value={range}
          onChange={setRange}
          options={[
            { value: '30d', label: '30 d' },
            { value: '90d', label: '90 d' },
            { value: '1y', label: '1 y' },
          ]}
        />
        <Select
          aria-label="Volume type"
          value={type}
          onChange={setType}
          options={(types.length ? types : [type]).map((t) => ({ value: t, label: volumeLabel(t) }))}
        />
        <span className="nyk-ui-hint">
          {workserver ? `reported by ${workserver}` : 'one report per day'} · UTC days
        </span>
      </div>
      <ErrorBox
        error={volumes.error ? statsError(volumes.error) : licences.error ? statsError(licences.error) : null}
      />
      <div className="nyk-card">
        <BarChart
          title={`${volumeLabel(type)} per day`}
          bars={totals.map((d) => ({ key: d.date, label: fmtDay(d.date), v: d.count }))}
          format={formatCount}
          empty="No daily metrics in this range."
        />
      </div>
      {!workserver && (
        <div className="nyk-card" data-testid="licences">
          <LineChart
            title="Licence usage"
            series={licenceSeries}
            from={Date.parse(`${w.fromDay}T00:00:00Z`)}
            to={Date.parse(`${w.toDay}T00:00:00Z`)}
            stepMs={86_400_000}
            format={formatCount}
            empty="No licence usage reported in this range."
          />
          {!!latest.length && (
            <TableScroller>
              <table className="nyk-ui-table nyk-cards">
                <thead>
                  <tr>
                    <th>Licence type</th>
                    <th className="sx-num">Latest</th>
                    <th className="sx-num">Peak in range</th>
                  </tr>
                </thead>
                <tbody>
                  {latest.map((l) => (
                    <tr key={l.type}>
                      <td className="nyk-card-title">{l.type || '—'}</td>
                      <td data-label="Latest" className="sx-num">
                        {fmtInt(l.last?.count)}{' '}
                        <span className="nyk-ui-hint">{l.last && fmtDay(l.last.date)}</span>
                      </td>
                      <td data-label="Peak in range" className="sx-num">
                        {fmtInt(l.peak)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          )}
        </div>
      )}
    </section>
  );
}

/** Error groups of one organization or installation (last 7 days), linking to the full list. */
export function ErrorGroupsSection({ customerId, workserver }: { customerId: number; workserver?: string }) {
  const groups = useAsync(
    () => stats.errorGroups({ customerId, workserver, status: 'active', sort: 'count', limit: 50 }),
    [customerId, workserver]
  );
  const base = `/statistics/errors?customerId=${customerId}${workserver ? `&workserver=${encodeURIComponent(workserver)}` : ''}`;
  return (
    <section className="sx-body" data-testid="error-groups">
      <ErrorBox error={groups.error} />
      <Loading loading={groups.loading && !groups.data} />
      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Active error groups, last 7 days</h2>
          <Link to={base} className="nyk-ui-hint">
            Open in Error groups
          </Link>
        </div>
        {groups.data && !groups.data.items.length && (
          <EmptyState title="No open errors in the last 7 days." />
        )}
        {!!groups.data?.items.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards">
              <thead>
                <tr>
                  <th>Error</th>
                  <th className="sx-num">Open</th>
                  <th className="sx-num">Total</th>
                  <th>Trend</th>
                  <th>WorkServers</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {groups.data.items.map((g) => (
                  <tr key={g.fingerprint}>
                    <td className="nyk-card-title sx-msg">
                      <Link
                        className="nyk-link sx-msg-clip"
                        to={`/statistics/errors/${encodeURIComponent(g.fingerprint)}${base.slice(base.indexOf('?'))}`}
                      >
                        {g.title}
                      </Link>
                      {g.new && <span className="nyk-ui-hint"> · new</span>}
                    </td>
                    <td data-label="Open" className="sx-num">
                      {fmtInt(g.open)}
                    </td>
                    <td data-label="Total" className="sx-num">
                      {fmtInt(g.count)}
                    </td>
                    <td data-label="Trend">
                      <Sparkline values={g.trend.map((t) => t.count)} label={`${g.count} in 7 days`} />
                    </td>
                    <td data-label="WorkServers" className="nyk-wrap">
                      {g.installations.map((i) => i.workserver ?? '?').join(', ')}
                    </td>
                    <td data-label="Last seen">{formatRelative(g.lastSeen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        )}
      </div>
    </section>
  );
}
